/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { useEffect, useState, useContext } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { filter, map, some, constant, get, groupBy, times, zipObject } from 'lodash';
import { useParams } from 'react-router-dom';
import { ActionButton, AlertBar } from '@blend/enterprise-components';

import { downloadDocument, createCollatedDocument } from 'blb-api/loans';
import { DisablednessContext } from 'blb-contexts/Disabledness';
import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { DocumentWithTag } from 'blb-ui-types/BridgeTypes';
import { removeExtensionFromFileName } from 'blb-utils/documents';
import { errorMessage } from 'blb-utils/errors';

import { mapDownloadableDocIds, isDocDownloadable } from '../../../docUtils/docUtils';
import { DocRowCluster } from './DocRowCluster';
import { useToasts } from 'blb-components/AuthenticatedApp/Toast';

const styles = StyleSheet.create({
  buttonWrapper: {
    marginRight: 8,
  },
  downloadButtonsWrapper: {
    display: 'flex',
  },
  errorContainer: {
    marginBottom: 8,
  },
  footer: {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 24,
  },
});

export namespace DocsTable {
  export interface Props {
    collateFileName: string;
    documentTypeLabel?: string;
    documents: DocumentWithTag[];
    handleClose: () => void;
    isUploaded: boolean;
  }
}

export const DocsTable: React.FC<DocsTable.Props> = ({
  collateFileName,
  documents,
  handleClose,
  isUploaded,
  documentTypeLabel,
}) => {
  const { loanId } = useParams<{ loanId: string }>();
  const [selectedDocs, setSelectedDocs] = useState<Record<string, boolean>>({});
  const [isDownloadAllInProgress, setDownloadAllInProgress] = useState<boolean>(false);
  const [isDownloadSelectedInProgress, setDownloadSelectedInProgress] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const { disabled, setDisabled } = useContext(DisablednessContext);
  const Toaster = useToasts();
  const me = useContext(MeContext);

  const areSomeDocsSelected = some(selectedDocs);

  const downloadableDocs = filter(documents, doc => isDocDownloadable(doc));

  const getDownloadFileName = (docs: BlendBridge.API.Document[]) => {
    if (docs.length === 1) return removeExtensionFromFileName(docs[0]);
    if (docs.length === downloadableDocs.length) return collateFileName;
    return `Selected ${collateFileName}`;
  };

  const getDownloadDocumentInfo = async (
    selectedDocsToDownload: BlendBridge.API.Document[],
  ): Promise<{ documentId: string; documentType: string }> => {
    if (selectedDocsToDownload.length === 1) {
      const document = selectedDocsToDownload[0];
      return { documentId: document._id, documentType: document.type };
    }
    const files = selectedDocsToDownload.map(doc => doc.primaryFile!.url);
    const { data: documentId } = await createCollatedDocument({
      collateFileName: getDownloadFileName(selectedDocsToDownload),
      files,
      loanId,
    });
    return { documentId, documentType: selectedDocsToDownload[0].type };
  };

  const downloadDocsFnGenerator = (
    inProgressSetter: React.Dispatch<React.SetStateAction<boolean>>,
    filesGenerator: () => BlendBridge.API.Document[],
  ) => async () => {
    try {
      setDisabled(true);
      inProgressSetter(true);
      const selectedDocsToDownload = filesGenerator();
      const { documentId, documentType } = await getDownloadDocumentInfo(selectedDocsToDownload);
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      downloadDocument({ documentId, documentTypeLabel: documentTypeLabel ?? documentType, loanId, Toaster });
    } catch (err) {
      setError(get(err, 'response.data.error.message', errorMessage('DOWNLOADING_FILE', me?.principalType)));
    } finally {
      setDisabled(false);
      inProgressSetter(false);
    }
  };
  const downloadSelected = downloadDocsFnGenerator(setDownloadSelectedInProgress, () =>
    filter(documents, ({ _id }) => selectedDocs[_id]),
  );
  const downloadAll = downloadDocsFnGenerator(setDownloadAllInProgress, constant(downloadableDocs));

  const docsBySentDate = groupBy(documents, 'sentOn');
  // ensure latest first order remains
  const sortedSentDateKeys = Object.keys(docsBySentDate).sort((a, b) => (a > b ? -1 : 1));

  useEffect(() => {
    const latestDownloadableDocIds = mapDownloadableDocIds(docsBySentDate[sortedSentDateKeys[0]]);
    setSelectedDocs(zipObject(latestDownloadableDocIds, times(latestDownloadableDocIds.length, constant(true))));
  }, []);

  return (
    <React.Fragment>
      {error && (
        <div className={css(styles.errorContainer)}>
          <AlertBar message={error} theme="error" />
        </div>
      )}
      {map(sortedSentDateKeys, (sentOn, idx) => (
        <DocRowCluster
          disabled={disabled}
          documentCluster={docsBySentDate[sentOn]}
          isFirst={idx === 0}
          isUploaded={isUploaded}
          key={sentOn}
          selectedDocs={selectedDocs}
          sentOn={sentOn}
          setSelectedDocs={setSelectedDocs}
        />
      ))}
      <div className={css(styles.footer)}>
        <div className={css(styles.downloadButtonsWrapper)}>
          <div className={css(styles.buttonWrapper)}>
            <ActionButton
              disabled={!areSomeDocsSelected || disabled}
              id="it-DocsTable-downloadSelected"
              isLoading={isDownloadSelectedInProgress}
              label="Download Selected"
              onClick={downloadSelected}
              theme="secondary"
            />
          </div>
          <ActionButton
            disabled={disabled}
            id="it-DocsTable-downloadAll"
            isLoading={isDownloadAllInProgress}
            label="Download all"
            onClick={downloadAll}
            theme="secondary"
          />
        </div>
        <div>
          <ActionButton
            disabled={disabled}
            id="it-DocsTable-close"
            label="Close"
            onClick={handleClose}
            theme="primary"
          />
        </div>
      </div>
    </React.Fragment>
  );
};

DocsTable.displayName = 'DocsTable';
